import { useEffect, useState } from 'react';
import { manageSharing } from './sharingService';

export interface InventoryTag {
  collectionId: string;
  tagId: string;
  name: string;
  collectionName?: string;
}
const emptySnapshot = {
  identity: '',
  tags: [] as InventoryTag[],
  completeCollectionIds: [] as string[],
};
/** Tag names are scoped to authenticated collection inventory, never shared-link state. */
export function useCollectionTags(userId: string, collectionIds: string[]) {
  const key = JSON.stringify(
    [...new Set(collectionIds)].filter(Boolean).sort(),
  );
  const [snapshot, setSnapshot] = useState(emptySnapshot);
  const identity = JSON.stringify([userId, key]);
  useEffect(() => {
    let active = true;
    const ids: string[] = JSON.parse(key);
    if (!ids.length) return;
    const load = async () => {
      const results = await Promise.all(
        ids.map(async (collectionId) => {
          try {
            const result = await manageSharing(collectionId, 'listTags');
            return {
              collectionId,
              tags: (result.tags || [])
                .filter((tag) => tag.state === 'active')
                .map((tag) => ({
                  collectionId,
                  tagId: tag.tagId,
                  name: tag.name,
                })),
            };
          } catch {
            return { collectionId, tags: undefined };
          }
        }),
      );
      if (active)
        setSnapshot((previous) => ({
          identity,
          tags: results.flatMap(
            (result) =>
              result.tags ??
              (previous.identity === identity
                ? previous.tags.filter(
                    (tag) => tag.collectionId === result.collectionId,
                  )
                : []),
          ),
          completeCollectionIds: results
            .filter((result) => result.tags !== undefined)
            .map((result) => result.collectionId),
        }));
    };
    void load();
    const refresh = () => {
      void load();
    };
    const tagsChanged = (event: Event) => {
      if (ids.includes((event as CustomEvent<string>).detail)) void load();
    };
    window.addEventListener('focus', refresh);
    window.addEventListener('qkindex.collectionTagsChanged', tagsChanged);
    return () => {
      active = false;
      window.removeEventListener('focus', refresh);
      window.removeEventListener('qkindex.collectionTagsChanged', tagsChanged);
    };
  }, [userId, key, identity]);
  return collectionIds.length && snapshot.identity === identity
    ? snapshot
    : emptySnapshot;
}
